import { useState } from 'react';
import { Send } from 'react-feather';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { swalMessage } from '@components/common';
import { opinionCreate } from '@lib/api';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import useAppStore from '@store/useAppStore';
import styled from 'styled-components';

const LENGTH_LIMIT = 2000;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PageBadge = styled.div`
  display: inline-block;
  align-self: flex-start;
  padding: 0.25rem 0.625rem;
  border: 1px solid #04c9db;
  border-radius: 0.25rem;
  color: #0f2a4a;
  font-size: 0.825rem;
  font-weight: 600;

  > span {
    color: #04c9db;
    margin-left: 0.25rem;
  }
`;

const Guide = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #555;
  line-height: 1.5;
  white-space: pre-line;
`;

const Textarea = styled.textarea`
  min-height: 13rem;
  resize: vertical;
  padding: 0.875rem;
  border: 1px solid #e3e3e3;
  border-radius: 0.5rem;
  background-color: #f8fcff;
  font-size: 0.95rem;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: #04c9db;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const CountText = styled.span`
  font-size: 0.825rem;
  color: #888;

  > b {
    color: #04c9db;
  }
`;

const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background-color: #04c9db;
  color: #fff;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.925rem;
  cursor: pointer;

  &:disabled {
    background-color: #c7c7c7;
    cursor: not-allowed;
  }

  > svg {
    width: 14px;
    height: 14px;
  }
`;

/**
 * 지점 의견 보내기 모달 (LETMEUP-167)
 * - 점주(지점 관리자)가 현재 페이지에 대한 의견을 남김
 * - 제출 시 `pageLocation` 에 현재 경로 자동 첨부
 */
const SendOpinionModal = ({ isOpen, onClose }) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const { merchantData } = useAppStore();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setText(e.target.value.slice(0, LENGTH_LIMIT));
  };

  const handleSubmit = async () => {
    const content = text.trim();
    if (!content) return;
    setSubmitting(true);
    try {
      await createRequest(opinionCreate, {
        content,
        pageLocation: typeof window !== 'undefined' ? window.location.pathname : '',
        merchantId: merchantData?.id ?? null,
      });
      swalMessage('success', '정상 접수되었습니다.\n소중한 의견 감사합니다.');
      setText('');
      onClose?.();
    } catch (error) {
      catchError(error, '의견 접수 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  return (
    <Modal centered isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader className="modal-title" toggle={onClose}>
        의견 보내기
      </ModalHeader>
      <ModalBody>
        <Wrapper>
          <PageBadge>
            현재 페이지: <span>{pathname || '-'}</span>
          </PageBadge>
          <Guide>
            {
              '보다 나은 서비스를 제공하기 위해 지점의 의견을 듣고 싶어요.\n이 페이지에서 불편한 점이나 개선 요청을 자유롭게 남겨주세요.'
            }
          </Guide>
          <Textarea
            value={text}
            onChange={handleChange}
            placeholder="소중한 의견을 남겨주세요."
          />
          <Footer>
            <CountText>
              <b>{text.length}</b> / {LENGTH_LIMIT}자
            </CountText>
            <SubmitBtn type="button" onClick={handleSubmit} disabled={submitting || !text.trim()}>
              <Send />
              보내기
            </SubmitBtn>
          </Footer>
        </Wrapper>
      </ModalBody>
    </Modal>
  );
};

export default SendOpinionModal;
